import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { useContent, photoUrl } from "../../context/ContentContext";
import { GALLERY } from "../../data/content";
import { Reveal, SectionHeading } from "../Reveal";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const FILTERS = ["all", "interior", "rooms", "kitchen", "outdoor"];
const SPANS = ["lg:col-span-7 lg:row-span-2", "lg:col-span-5", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-12"];

export const Gallery = () => {
  const { t } = useLang();
  const { content } = useContent();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null); // index into filtered

  const source = content.photos && content.photos.length
    ? content.photos.map((p, i) => ({ src: photoUrl(p.id), cat: p.cat, span: SPANS[i % SPANS.length] }))
    : GALLERY;
  const filtered = filter === "all" ? source : source.filter((g) => g.cat === filter);

  const openAt = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const prev = () => setLightbox((i) => (i - 1 + filtered.length) % filtered.length);
  const next = () => setLightbox((i) => (i + 1) % filtered.length);

  return (
    <section id="gallery" className="scroll-mt-20 bg-[#FAF8F5] px-4 sm:px-8 py-20 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <SectionHeading eyebrow={t("gallery.eyebrow")} title={t("gallery.title")} subtitle={t("gallery.subtitle")} />
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`gallery-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  filter === f
                    ? "bg-stone-900 text-white"
                    : "border border-stone-300 bg-white text-stone-600 hover:border-stone-400"
                }`}
              >
                {t(`gallery.filters.${f}`)}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 grid auto-rows-[200px] grid-cols-1 gap-4 sm:grid-cols-2 lg:auto-rows-[220px] lg:grid-cols-12">
          {filtered.map((g, i) => (
            <Reveal
              key={g.src}
              delay={Math.min(i * 0.05, 0.3)}
              className={`group relative overflow-hidden rounded-2xl ${g.span || "lg:col-span-4"}`}
            >
              <button
                data-testid={`gallery-image-${i}`}
                onClick={() => openAt(i)}
                className="h-full w-full"
              >
                <img
                  src={g.src}
                  alt={t(`gallery.filters.${g.cat}`)}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/20" />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="max-w-5xl border-none bg-transparent p-0 shadow-none"
          data-testid="lightbox-modal"
        >
          <DialogTitle className="sr-only">{t("gallery.title")}</DialogTitle>
          {lightbox !== null && (
            <div className="relative">
              <img
                src={filtered[lightbox].src}
                alt=""
                className="max-h-[80vh] w-full rounded-xl object-contain"
              />
              <button
                data-testid="lightbox-close-btn"
                onClick={close}
                className="absolute -top-2 -right-2 rounded-full bg-white p-2 text-stone-800 shadow-lg hover:bg-stone-100"
              >
                <X className="h-5 w-5" />
              </button>
              <button
                data-testid="lightbox-prev-btn"
                onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2.5 text-stone-800 shadow-lg hover:bg-white"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                data-testid="lightbox-next-btn"
                onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2.5 text-stone-800 shadow-lg hover:bg-white"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
